/* ==========================================================================
   Hero slideshow counter — Smart Swimsuits custom
   Builds the 01/02/03 control with a countdown ring and wires it to Savor's
   <slideshow-component>.

   TIMING: Savor's autoplay is a free-running setInterval that starts once,
   while 'slideshow:select' fires only after the slide transition completes.
   Running the ring off that event left the two clocks drifting apart by the
   transition duration, which read as a pause once the ring closed. So the
   theme's interval is switched off and the ring itself drives the advance —
   one clock, no gap.
   ========================================================================== */
(() => {
  const SECTION_ID = 'hero_slideshow';
  const R = 15.5; // ring radius
  const CIRC = 2 * Math.PI * R;

  function build(section) {
    const slideshow = section.querySelector('slideshow-component');
    if (!slideshow || section.querySelector('.ss-counter')) return;

    const slides = [...slideshow.querySelectorAll('slideshow-slide')];
    if (slides.length < 2) return;

    // Match the ring sweep to the section's configured autoplay speed.
    const speed = Number(slideshow.getAttribute('autoplay')) || 6;
    section.style.setProperty('--ss-duration', `${speed}s`);

    // If the visitor has asked for reduced motion the ring never animates, so
    // it can't drive anything — leave the theme's own autoplay in charge.
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ringDrives = Boolean(slideshow.getAttribute('autoplay')) && !reducedMotion;

    const counter = document.createElement('div');
    counter.className = 'ss-counter';
    counter.innerHTML = `
      <svg class="ss-counter__ring" viewBox="0 0 34 34" aria-hidden="true">
        <circle class="ss-counter__track" cx="17" cy="17" r="${R}"></circle>
        <circle class="ss-counter__progress" cx="17" cy="17" r="${R}" style="--ss-circ:${CIRC}"></circle>
      </svg>
      <div class="ss-counter__nums" role="tablist" aria-label="Slides"></div>
    `;

    const nums = counter.querySelector('.ss-counter__nums');
    slides.forEach((_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'ss-counter__num';
      b.textContent = String(i + 1).padStart(2, '0');
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-label', `Go to slide ${i + 1}`);
      b.addEventListener('click', () => slideshow.select(i, new Event('click')));
      nums.appendChild(b);
    });

    section.appendChild(counter);

    const ring = counter.querySelector('.ss-counter__progress');
    const buttons = [...nums.children];

    if (ringDrives) {
      // Take the theme's interval out of the loop.
      slideshow.pause();

      // The ring closing IS the cue to advance — no intermediate timer.
      // If the ring completes while the tab is hidden we must not advance (the
      // visitor would come back mid-sequence), but we also cannot simply drop
      // the event: sync() is what restarts the sweep, and sync() only runs on
      // select. Returning early therefore left the ring sitting closed and
      // autoplay dead until the visitor clicked something. Remember the missed
      // advance and honour it when the tab comes back.
      let advanceWhenVisible = false;

      ring.addEventListener('animationend', () => {
        if (document.hidden) {
          advanceWhenVisible = true;
          return;
        }
        slideshow.next();
      });

      // Preserve Savor's hover-to-pause behaviour by freezing the sweep.
      const setState = (state) => {
        ring.style.animationPlayState = state;
      };
      slideshow.addEventListener('mouseenter', () => setState('paused'));
      slideshow.addEventListener('mouseleave', () => setState('running'));
      document.addEventListener('visibilitychange', () => {
        setState(document.hidden ? 'paused' : 'running');
        if (!document.hidden && advanceWhenVisible) {
          advanceWhenVisible = false;
          slideshow.next();
        }
      });
    }

    const sync = (index) => {
      buttons.forEach((b, i) => b.setAttribute('aria-current', String(i === index)));

      // Keep the theme's interval off — some paths inside the component call
      // resume() and would otherwise reintroduce the second clock.
      if (ringDrives && !slideshow.paused) slideshow.pause();

      // Restart the sweep. Removing the class and forcing reflow resets the
      // animation, which a plain class re-add would not do.
      ring.classList.remove('is-running');
      void ring.getBoundingClientRect();
      ring.classList.add('is-running');

      slides.forEach((s) => s.removeAttribute('data-ss-entering'));
      const active = slides[index];
      if (active) {
        active.setAttribute('data-ss-entering', '');

        // animationend bubbles, so any animated descendant inside the slide —
        // a button transition, an app embed — could fire this first and strip
        // the attribute while the reveal was still running, snapping the copy
        // to its end state. Only the reveal on .slide__content should end it.
        const onRevealEnd = (e) => {
          if (e.animationName !== 'ss-rise') return;
          active.removeAttribute('data-ss-entering');
          active.removeEventListener('animationend', onRevealEnd);
        };
        active.addEventListener('animationend', onRevealEnd);
      }
    };

    slideshow.addEventListener('slideshow:select', (e) => {
      const i = e?.detail?.index;
      if (typeof i === 'number') sync(i);
    });

    sync(slideshow.current ?? 0);
  }

  const init = () => {
    // Sections in a JSON template render as
    // shopify-section-template--<id>__hero_slideshow, so match on the suffix.
    const section =
      document.querySelector(`[id^="shopify-section"][id$="${SECTION_ID}"]`) ||
      document.getElementById(`shopify-section-${SECTION_ID}`);
    if (section) {
      section.classList.add('ss-hero');
      build(section);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section on every settings change.
  document.addEventListener('shopify:section:load', (e) => {
    if (String(e.detail?.sectionId || '').endsWith(SECTION_ID)) init();
  });
})();
